/**
 * Interactive building blocks: buttons, chips, tickets, tiles, nav, forms, overlays.
 * Real <button>/<a>/<input> elements with labels and ≥44px touch targets (SPEC §17.3).
 * Router-agnostic: anything that navigates takes an `as` link component (React Router's
 * <Link> in the apps), defaulting to a plain <a>.
 */
import {
  useEffect,
  useId,
  useRef,
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type ComponentType,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { Icon, type IconName } from './Icon';
import { StripeBand } from './motifs';

export type LinkLike = ComponentType<AnchorHTMLAttributes<HTMLAnchorElement> & { to: string }>;

function PlainLink({ to, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement> & { to: string }) {
  return <a href={to} {...rest} />;
}

// ------------------------------------------------------------------------------- Button

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'quiet';

interface ButtonBase {
  variant?: ButtonVariant;
  size?: 'lg' | 'md' | 'sm';
  iconRight?: IconName;
  iconLeft?: IconName;
  block?: boolean;
  children: ReactNode;
}

export type ButtonProps = ButtonBase & ButtonHTMLAttributes<HTMLButtonElement>;

function classes(variant: ButtonVariant, size: string, block: boolean | undefined, extra?: string) {
  return ['pmh-btn', `pmh-btn--${variant}`, `pmh-btn--${size}`, block ? 'pmh-btn--block' : '', extra ?? '']
    .filter(Boolean)
    .join(' ');
}

/** Primary = rust (cream text passes AA), secondary = pine, ghost = cream card, quiet = text. */
export function Button({
  variant = 'primary',
  size = 'lg',
  iconLeft,
  iconRight,
  block,
  children,
  className,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button type={type} className={classes(variant, size, block, className)} {...rest}>
      {iconLeft && <Icon name={iconLeft} />}
      <span>{children}</span>
      {iconRight && <Icon name={iconRight} strokeWidth={2.2} />}
    </button>
  );
}

export function ButtonLink({
  to,
  as: L = PlainLink,
  variant = 'primary',
  size = 'lg',
  iconLeft,
  iconRight,
  block,
  children,
  className,
  ...rest
}: ButtonBase & AnchorHTMLAttributes<HTMLAnchorElement> & { to: string; as?: LinkLike }) {
  return (
    <L to={to} className={classes(variant, size, block, className)} {...rest}>
      {iconLeft && <Icon name={iconLeft} />}
      <span>{children}</span>
      {iconRight && <Icon name={iconRight} strokeWidth={2.2} />}
    </L>
  );
}

export function BackButton({ onClick, label = 'Back' }: { onClick: () => void; label?: string }) {
  return (
    <button type="button" className="pmh-back" aria-label={label} onClick={onClick}>
      <Icon name="back" />
    </button>
  );
}

// --------------------------------------------------------------------------------- Chip

export type ChipTone = 'cream' | 'mustard' | 'pine' | 'rust' | 'ink';

export function Chip({
  pressed,
  onToggle,
  tone = 'cream',
  selectedTone = 'mustard',
  children,
  disabled,
}: {
  pressed?: boolean;
  onToggle?: (next: boolean) => void;
  tone?: ChipTone;
  selectedTone?: ChipTone;
  children: ReactNode;
  disabled?: boolean;
}) {
  if (!onToggle) {
    return <span className={`pmh-chip pmh-chip--${tone} pmh-chip--static`}>{children}</span>;
  }
  return (
    <button
      type="button"
      className={`pmh-chip pmh-chip--${pressed ? selectedTone : tone}`}
      aria-pressed={pressed}
      disabled={disabled}
      onClick={() => onToggle(!pressed)}
    >
      {children}
    </button>
  );
}

/** "Oct 16 → 19 · 3 nights · 4 of you" (SPEC §15 Welcome). */
export function StayChips({ items }: { items: { label: string; tone: ChipTone }[] }) {
  return (
    <ul className="pmh-chiprow" aria-label="Your stay">
      {items.map((it) => (
        <li key={it.label}>
          <Chip tone={it.tone}>{it.label}</Chip>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------- ProgressStripes

export function ProgressStripes({ step, of }: { step: number; of: number }) {
  const colors = ['var(--rust)', 'var(--mustard)', 'var(--pine)'];
  return (
    <div className="pmh-progress">
      <div
        className="pmh-progress__bars"
        role="progressbar"
        aria-valuemin={1}
        aria-valuemax={of}
        aria-valuenow={step}
        aria-label={`Step ${step} of ${of}`}
      >
        {Array.from({ length: of }, (_, i) => (
          <div key={i} style={{ background: i < step ? colors[i % colors.length] : 'var(--line)' }} />
        ))}
      </div>
      <span className="pmh-progress__label" aria-hidden="true">
        {step} of {of}
      </span>
    </div>
  );
}

/** Numbered steps with colored discs (Welcome; guide detail). */
export function NumberedSteps({ steps }: { steps: ReactNode[] }) {
  const tones = ['rust', 'mustard', 'pine'];
  return (
    <ol className="pmh-steps">
      {steps.map((s, i) => (
        <li key={i}>
          <span className={`pmh-steps__n pmh-steps__n--${tones[i % 3]}`} aria-hidden="true">
            {i + 1}
          </span>
          <span>{s}</span>
        </li>
      ))}
    </ol>
  );
}

// --------------------------------------------------------------------------- TicketStub

/**
 * A ticket with a dashed perforation and circular notches cut from the page color — a nod
 * to the Opera House and gold-rush tickets (SPEC §17.3). Upsells and order rows use it.
 */
export function TicketStub({
  children,
  stub,
  stubWidth = 94,
  notchColor = 'var(--bg)',
}: {
  children: ReactNode;
  stub: ReactNode;
  stubWidth?: number;
  notchColor?: string;
}) {
  return (
    <div className="pmh-ticket">
      <div className="pmh-ticket__main">{children}</div>
      <div className="pmh-ticket__perf" aria-hidden="true" />
      <div
        className="pmh-ticket__notch pmh-ticket__notch--top"
        style={{ right: stubWidth - 8, background: notchColor }}
        aria-hidden="true"
      />
      <div
        className="pmh-ticket__notch pmh-ticket__notch--bottom"
        style={{ right: stubWidth - 8, background: notchColor }}
        aria-hidden="true"
      />
      <div className="pmh-ticket__stub" style={{ width: stubWidth }}>
        {stub}
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------------- Tile

export type TileTone = 'pine' | 'rust' | 'mustard' | 'ink';

const TILE_DISC: Record<TileTone, string> = {
  pine: 'pmh-tile__disc--pine',
  rust: 'pmh-tile__disc--rust',
  mustard: 'pmh-tile__disc--orange',
  ink: 'pmh-tile__disc--mustard',
};

export function Tile({
  to,
  as: L = PlainLink,
  tone,
  icon,
  title,
  subtitle,
}: {
  to: string;
  as?: LinkLike;
  tone: TileTone;
  icon: IconName;
  title: ReactNode;
  subtitle?: ReactNode;
}) {
  return (
    <L to={to} className={`pmh-tile pmh-tile--${tone}`}>
      <span className={`pmh-tile__disc ${TILE_DISC[tone]}`}>
        <Icon name={icon} />
      </span>
      <span className="pmh-tile__text">
        <span className="pmh-tile__title">{title}</span>
        {subtitle && <span className="pmh-tile__subtitle">{subtitle}</span>}
      </span>
    </L>
  );
}

// ---------------------------------------------------------------------------- BottomNav

export interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  active: boolean;
  /** Drawn as a pill that asks to be tapped (the guest's My Stay while signed out). */
  highlight?: boolean;
}

/** The app's tabs on dark chocolate, with the stripe band above (SPEC §15). */
export function BottomNav({ items, as: L = PlainLink }: { items: NavItem[]; as?: LinkLike }) {
  return (
    <div className="pmh-bottomnav">
      <StripeBand />
      <nav aria-label="Main" className="pmh-bottomnav__bar">
        {items.map((it) => (
          <L
            key={it.to}
            to={it.to}
            aria-current={it.active ? 'page' : undefined}
            className={`pmh-bottomnav__item${it.active ? ' is-active' : ''}${it.highlight ? ' is-highlight' : ''}`}
          >
            <Icon name={it.icon} size={22} strokeWidth={it.active || it.highlight ? 2 : 1.8} />
            {it.label}
          </L>
        ))}
      </nav>
    </div>
  );
}

// ------------------------------------------------------------------------------- Forms

export function TextField({
  label,
  hint,
  error,
  handwritten,
  ...rest
}: { label: string; hint?: string; error?: string; handwritten?: boolean } & InputHTMLAttributes<HTMLInputElement>) {
  const id = useId();
  return (
    <div className="pmh-field">
      <label htmlFor={id} className="pmh-field__label">
        {label}
      </label>
      <input
        id={id}
        className={`pmh-input${handwritten ? ' pmh-input--note' : ''}`}
        aria-invalid={error ? true : undefined}
        aria-describedby={error || hint ? `${id}-help` : undefined}
        {...rest}
      />
      {(error || hint) && (
        <span id={`${id}-help`} className={error ? 'pmh-field__error' : 'pmh-field__hint'}>
          {error ?? hint}
        </span>
      )}
    </div>
  );
}

export function TextArea({
  label,
  hint,
  ...rest
}: { label: string; hint?: string } & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const id = useId();
  return (
    <div className="pmh-field">
      <label htmlFor={id} className="pmh-field__label">
        {label}
      </label>
      <textarea id={id} className="pmh-input pmh-input--area" placeholder={hint} {...rest} />
    </div>
  );
}

export function SearchField({
  label,
  ...rest
}: { label: string } & InputHTMLAttributes<HTMLInputElement>) {
  const id = useId();
  return (
    <div className="pmh-search">
      <label htmlFor={id} className="pmh-sr-only">
        {label}
      </label>
      <Icon name="search" size={22} />
      <input id={id} type="search" className="pmh-search__input" {...rest} />
    </div>
  );
}

export function Checkbox({
  label,
  checked,
  onChange,
}: {
  label: ReactNode;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className="pmh-check">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="pmh-check__box" aria-hidden="true">
        <Icon name="check" size={18} strokeWidth={3} />
      </span>
      <span>{label}</span>
    </label>
  );
}

export function RadioCard({
  name,
  value,
  checked,
  onChange,
  label,
  aside,
}: {
  name: string;
  value: string;
  checked: boolean;
  onChange: (value: string) => void;
  label: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <label className={`pmh-radio${checked ? ' is-checked' : ''}`}>
      <input type="radio" name={name} value={value} checked={checked} onChange={() => onChange(value)} />
      <span className="pmh-radio__dot" aria-hidden="true" />
      <span className="pmh-radio__label">{label}</span>
      {aside && <span className="pmh-radio__aside">{aside}</span>}
    </label>
  );
}

// ---------------------------------------------------------------------------- Overlays

/** A bottom sheet built on <dialog> so focus trapping and Escape come from the browser. */
export function SheetModal({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      if (typeof d.showModal === 'function') d.showModal();
      else d.setAttribute('open', '');
    } else if (!open && d.open) {
      if (typeof d.close === 'function') d.close();
      else d.removeAttribute('open');
    }
  }, [open]);
  return (
    <dialog ref={ref} className="pmh-sheet" aria-label={title} onClose={onClose} onCancel={onClose}>
      <div className="pmh-sheet__head">
        <h2 className="pmh-display" style={{ fontSize: 26 }}>
          {title}
        </h2>
        <button type="button" className="pmh-back" aria-label="Close" onClick={onClose}>
          <Icon name="close" />
        </button>
      </div>
      {children}
    </dialog>
  );
}

export function Toast({ message, tone = 'pine' }: { message: string | null; tone?: 'pine' | 'rust' }) {
  return (
    <div className="pmh-toast-region" role="status" aria-live="polite">
      {message && <div className={`pmh-toast pmh-toast--${tone}`}>{message}</div>}
    </div>
  );
}

export function EmptyState({ title, note, children }: { title: string; note?: string; children?: ReactNode }) {
  return (
    <div className="pmh-empty">
      <h2 className="pmh-display" style={{ fontSize: 28 }}>
        {title}
      </h2>
      {note && <p className="pmh-note" style={{ color: 'var(--rust)', fontSize: 22 }}>{note}</p>}
      {children}
    </div>
  );
}

export function Skeleton({ height = 18, width = '100%', radius = 12 }: { height?: number; width?: number | string; radius?: number }) {
  return <div className="pmh-skeleton" style={{ height, width, borderRadius: radius }} aria-hidden="true" />;
}
